"use client";

import Link from "next/link";
import { useState } from "react";
import { ExternalLink, MessageSquare, Bookmark, Share2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { PostBody } from "@/components/posts/PostBody";
import { formatRelativeDate } from "@/lib/format";
import type { NewsFeedPost, NewsFeedSource } from "@/lib/api";

/**
 * Twitter-style news-feed card. One row per admin-published news-hub
 * cluster summary; the card carries:
 *
 *   Header
 *     - Author avatar + display name + @handle
 *     - Verified-expert checkmark when applicable
 *     - Topic chip (primary topic from topic_clusters is_primary)
 *     - Relative timestamp (e.g. "5m ago")
 *
 *   Body
 *     - Markdown-rendered post body (sanitized HTML from backend)
 *     - 4-line clamp with "Show more" affordance for long summaries
 *
 *   Sources (footer)
 *     - Up to 3 chips: favicon + sourceName + ExternalLink icon
 *     - Each chip opens in a new tab; rel=noopener for safety
 *
 *   Action bar
 *     - Reply (deep-links to /posts/:id which has the canonical
 *       comment thread)
 *     - Bookmark (toggle handled in /posts/:id; the card chip is
 *       a deep-link affordance, not an inline mutation, to keep
 *       the news-feed surface read-only)
 *     - Share (uses navigator.share when available; falls back to
 *       copy-link)
 *
 * Mobile-first per CLAUDE.md — the card stacks at 375px and gets
 * comfortable padding at ≥768px.
 */
export function NewsFeedCard({ post }: { post: NewsFeedPost }) {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const handle = post.authorUsername || "anonymous";
  const display = post.authorDisplayName || handle;
  const initial = (display || handle).charAt(0).toUpperCase();
  const rel = formatRelativeDate(post.createdAt, i18n.language || "en");
  const postUrl = `/posts/${post.id}`;

  // Truncate to the first 4 lines visually. We use a CSS clamp on
  // the body container; the "Show more" toggle releases the clamp.
  // The PostBody component already renders the sanitized HTML — we
  // wrap it in a clamp container to keep the card scannable.

  const handleShare = () => {
    const url =
      typeof window !== "undefined"
        ? `${window.location.origin}${postUrl}`
        : postUrl;
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      void navigator
        .share({ title: post.title ?? display, url })
        .catch(() => {
          // User cancelled or share unsupported — fall through to clipboard.
          if (typeof navigator.clipboard !== "undefined") {
            void navigator.clipboard.writeText(url);
          }
        });
    } else if (
      typeof navigator !== "undefined" &&
      typeof navigator.clipboard !== "undefined"
    ) {
      void navigator.clipboard.writeText(url);
    }
  };

  return (
    <article
      className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-3 transition-colors hover:border-[var(--color-border-strong)] md:p-4"
      data-testid="news-feed-card"
    >
      {/* Header */}
      <header className="mb-2 flex items-center gap-2">
        <Link href={`/u/${handle}`} className="flex shrink-0 items-center">
          {post.authorAvatarUrl ? (
            // Avatar URL is server-validated (https://) per CLAUDE.md
            // photo discipline; plain img tag avoids next/image's
            // remotePatterns config for avatar-host onboarding cost.
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={post.authorAvatarUrl}
              alt=""
              className="h-9 w-9 rounded-full object-cover md:h-10 md:w-10"
            />
          ) : (
            <span
              className="flex h-9 w-9 items-center justify-center rounded-full text-[13px] font-bold text-white md:h-10 md:w-10"
              style={{
                background: "linear-gradient(135deg, #8957e5, #ec4899)",
              }}
              aria-hidden="true"
            >
              {initial}
            </span>
          )}
        </Link>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
            <Link
              href={`/u/${handle}`}
              className="truncate text-[13px] font-bold text-[var(--color-text)] hover:underline md:text-[14px]"
            >
              {display}
            </Link>
            {post.authorVerifiedExpert && (
              <span
                className="text-[11px] text-[var(--color-brand)]"
                title={t("newsFeed.verifiedExpert", "Verified expert")}
              >
                {/* Inline check glyph rather than a heavy lucide import for one row */}
                ✓
              </span>
            )}
            <Link
              href={`/u/${handle}`}
              className="truncate text-[11px] text-[var(--color-text3)] hover:underline md:text-[12px]"
            >
              @{handle}
            </Link>
            <span aria-hidden="true" className="text-[11px] text-[var(--color-text3)]">
              ·
            </span>
            <time
              dateTime={post.createdAt}
              className="text-[11px] text-[var(--color-text3)] md:text-[12px]"
            >
              {rel}
            </time>
            {post.topic && (
              <>
                <span
                  aria-hidden="true"
                  className="text-[11px] text-[var(--color-text3)]"
                >
                  ·
                </span>
                <Link
                  href={`/feed/news?topicSlug=${encodeURIComponent(post.topic.slug)}`}
                  className="rounded-full border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.4px] text-[var(--color-text2)] transition-colors hover:border-[var(--color-brand)] hover:text-[var(--color-brand)] md:text-[11px]"
                >
                  {post.topic.name}
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Body — markdown-rendered, line-clamp until expanded */}
      <Link href={postUrl} className="block">
        <div
          className={
            expanded
              ? "text-[14px] leading-relaxed text-[var(--color-text)] md:text-[15px]"
              : "line-clamp-4 text-[14px] leading-relaxed text-[var(--color-text)] md:text-[15px]"
          }
        >
          <PostBody html={post.bodyHtml} previewOutboundLinks={false} />
        </div>
      </Link>
      {/* Show-more toggle. Only renders when the body is long enough
          to clamp — we render unconditionally since the clamp itself
          gates visibility; the toggle's a no-op on short posts but
          stays harmless. */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="mt-1 text-[11px] font-bold text-[var(--color-brand)] hover:underline md:text-[12px]"
        aria-expanded={expanded}
      >
        {expanded
          ? t("newsFeed.showLess", "Show less")
          : t("newsFeed.showMore", "Show more")}
      </button>

      {/* Sources footer — 3 chips max, each opens in a new tab */}
      {post.sources.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-1.5">
          {post.sources.slice(0, 3).map((source, idx) => (
            <li key={`${post.id}-source-${idx}`}>
              <SourceChip source={source} />
            </li>
          ))}
        </ul>
      )}

      {/* Action bar */}
      <div className="mt-3 flex items-center gap-1 border-t border-[var(--color-border)] pt-2">
        <Link
          href={`${postUrl}#comments`}
          className="inline-flex min-h-[36px] items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-bold text-[var(--color-text3)] transition-colors hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]"
          aria-label={t("newsFeed.reply", "Reply")}
        >
          <MessageSquare size={14} strokeWidth={1.75} aria-hidden="true" />
          <span>{t("newsFeed.reply", "Reply")}</span>
        </Link>
        <button
          type="button"
          onClick={handleShare}
          className="inline-flex min-h-[36px] items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-bold text-[var(--color-text3)] transition-colors hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]"
          aria-label={t("newsFeed.share", "Share")}
        >
          <Share2 size={14} strokeWidth={1.75} aria-hidden="true" />
          <span>{t("newsFeed.share", "Share")}</span>
        </button>
        <Link
          href={postUrl}
          className="ml-auto inline-flex min-h-[36px] items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-bold text-[var(--color-text3)] transition-colors hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]"
          aria-label={t("newsFeed.bookmark", "Bookmark")}
        >
          <Bookmark size={14} strokeWidth={1.75} aria-hidden="true" />
          <span>{t("newsFeed.bookmark", "Bookmark")}</span>
        </Link>
      </div>
    </article>
  );
}

/**
 * One source-attribution chip. Renders the publisher favicon (via
 * Google's s2 favicon service — same pattern as LinkPreviewCard),
 * the source name, and an outbound-link icon. Clicking opens the
 * article URL in a new tab.
 */
function SourceChip({ source }: { source: NewsFeedSource }) {
  let host = "";
  try {
    host = new URL(source.url).hostname.replace(/^www\./, "");
  } catch {
    host = "";
  }
  const favicon = host
    ? `https://www.google.com/s2/favicons?domain=${host}&sz=32`
    : null;

  return (
    <a
      href={source.url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex max-w-[260px] items-center gap-1.5 rounded-full border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1 text-[11px] font-medium text-[var(--color-text2)] transition-colors hover:border-[var(--color-brand)] hover:text-[var(--color-brand)] md:max-w-[320px]"
      title={source.title}
    >
      {favicon ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={favicon}
          alt=""
          className="h-4 w-4 shrink-0 rounded-sm"
          aria-hidden="true"
        />
      ) : (
        <span className="h-4 w-4 shrink-0 rounded-sm bg-[var(--color-border)]" />
      )}
      <span className="truncate">{source.sourceName}</span>
      <ExternalLink
        size={11}
        strokeWidth={1.75}
        aria-hidden="true"
        className="shrink-0 text-[var(--color-text3)]"
      />
    </a>
  );
}
